import React, { useState, useEffect } from 'react'
import { Container, Icon } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import InteractiveDemo from '../HomePage/InteractiveDemo'
import DemoPreview from '../HomePage/DemoPreview'
import './CreatorLandingPage.css'

const CreatorLandingPage = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [prompt, setPrompt] = useState('')
    const [banner, setBanner] = useState(null)

    useEffect(() => {
        const banners = [
            './Banners/20250303_105847_7457888e-4516-41f0-ada4-ee333894a438.jpg',
            './Banners/20250303_110636_d7204dab-b2cf-4b9c-bb68-a36a395905de.jpg',
            './Banners/20250303_111142_050fe721-fb23-4b3b-b24f-5a26c72a142b.jpg',
            './Banners/20250303_112346_6728e024-67fa-4a57-9e1b-11abcb56a071.jpg',
            './Banners/20250303_112617_0953be47-9fd9-496e-9d3a-1566a38f1224.jpg',
            './Banners/20250303_113012_6e6c3206-7f5a-48a4-872d-6da65f0714b3.jpg',
            './Banners/20250303_142256_d57c448a-90ae-48ad-832f-b0de2cd143f2.jpg',
            './Banners/20260120_1836_Image Generation_simple_compose_01kfe7kv68fjr8wczfmq587afh.webp',
            './Banners/20260120_1356_Image Generation_simple_compose_01kfdqjxkjetj8w8pa0j8eqgep.webp',
            './Banners/20260120_1836_Image Generation_simple_compose_01kfe7kv67eef8v2823q36380c.webp',
            './Banners/20260120_1356_Image Generation_simple_compose_01kfdqjxkkfzf85xkcaad3jknw.webp',
        ]
        const randomBanner = banners[Math.floor(Math.random() * banners.length)]
        setBanner(randomBanner)
        
        window.scrollTo(0, 0)
    }, [])

    const handleCreate = () => {
        navigate('/Create/Generate', { state: { prompt } })
    }

    const handleAdvancedMode = () => {
        navigate('/Create/Edit/new')
    }

    const features = [
        {
            icon: 'magic',
            title: t('creatorFeature1Title'),
            desc: t('creatorFeature1Desc')
        },
        {
            icon: 'pencil',
            title: t('creatorFeature2Title'),
            desc: t('creatorFeature2Desc')
        },
        {
            icon: 'images',
            title: t('creatorFeature3Title'),
            desc: t('creatorFeature3Desc')
        }
    ]

    const steps = [
        {
            title: t('creatorStep1Title'),
            desc: t('creatorStep1Desc')
        },
        {
            title: t('creatorStep2Title'),
            desc: t('creatorStep2Desc')
        },
        {
            title: t('creatorStep3Title'),
            desc: t('creatorStep3Desc')
        },
        {
            title: t('creatorStep4Title'),
            desc: t('creatorStep4Desc')
        }
    ]

    return (
        <div className="creator-landing-page">
            {/* Hero Section */}
            <section className="creator-hero-section">
                {banner && (
                    <div className="creator-hero-background">
                        <img src={banner} className="creator-hero-image" alt="Fablia Creation" />
                        <div className="creator-hero-overlay" />
                    </div>
                )}
                <Container className="creator-hero-content">
                    <h1 className="creator-hero-title">
                        {t('creatorHeroTitle')}
                    </h1>
                    <p className="creator-hero-subtitle">
                        {t('creatorHeroSubtitle')}
                    </p>

                    <div className="creator-prompt-container">
                        <label className="creator-prompt-label">
                            {t('creatorPromptLabel')}
                        </label>
                        <textarea 
                            className="creator-prompt-input"
                            placeholder={t('DescribeYourStoryPlaceholder')}
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                        />
                        <div className="creator-actions">
                            <button className="creator-cta-button" onClick={handleCreate}>
                                <Icon name="magic" />
                                {t('creatorCtaButton')}
                            </button>
                            <button className="creator-secondary-link" onClick={handleAdvancedMode}>
                                {t('creatorAdvancedMode')} →
                            </button>
                        </div>
                    </div>
                    
                    <span className="creator-cta-subtext">
                        {t('creatorCtaSubtext')}
                    </span>
                </Container>
                <button 
                    className="scroll-indicator" 
                    onClick={() => document.querySelector('.creator-features-section')?.scrollIntoView({ behavior: 'smooth' })}
                    aria-label="Scroll down"
                >
                    <Icon name="chevron down" />
                </button>
            </section>

            {/* Features Section */}
            <section className="creator-features-section">
                <Container>
                    <h2 className="creator-section-heading">{t('creatorFeaturesTitle')}</h2>
                    <div className="creator-features-grid">
                        {features.map((feature, index) => (
                            <div className="creator-feature-card" key={index}>
                                <div className="creator-feature-icon">
                                    <Icon name={feature.icon} />
                                </div>
                                <h3 className="creator-feature-title">{feature.title}</h3>
                                <p className="creator-feature-desc">{feature.desc}</p>
                            </div>
                        ))}
                    </div>
                </Container>
                <button 
                    className="scroll-indicator section-scroll" 
                    onClick={() => document.querySelector('.creator-demo-section')?.scrollIntoView({ behavior: 'smooth' })}
                    aria-label="Scroll down"
                >
                    <Icon name="chevron down" />
                </button>
            </section>

            {/* Demo Section */}
            <section className="creator-demo-section">
                <Container>
                    <h2 className="creator-section-heading">{t('creatorDemoTitle')}</h2>
                    <p className="creator-section-subheading">
                        {t('creatorDemoSubtitle')}
                    </p>
                    <InteractiveDemo storyId="Za2kgJ5eqkuBETwf1CMg" />
                </Container>
                <button 
                    className="scroll-indicator section-scroll" 
                    onClick={() => document.querySelector('.creator-genres-section')?.scrollIntoView({ behavior: 'smooth' })}
                    aria-label="Scroll down"
                >
                    <Icon name="chevron down" />
                </button>
            </section>

            {/* Genres Section */}
            <section className="creator-genres-section">
                <Container>
                    <h2 className="creator-section-heading">{t('creatorGenresTitle')}</h2>
                    <p className="creator-section-subheading">
                        {t('creatorGenresSubtitle')}
                    </p>
                    <DemoPreview />
                </Container>
                <button 
                    className="scroll-indicator section-scroll" 
                    onClick={() => document.querySelector('.creator-duplicate-section')?.scrollIntoView({ behavior: 'smooth' })}
                    aria-label="Scroll down"
                >
                    <Icon name="chevron down" />
                </button>
            </section>

            {/* Duplicate Section */}
            <section className="creator-duplicate-section">
                <Container>
                    <h2 className="creator-section-heading">{t('creatorDuplicateTitle')}</h2>
                    <p className="creator-section-subheading">
                        {t('creatorDuplicateSubtitle')}
                    </p>
                    <div className="creator-duplicate-content">
                        <div className="creator-duplicate-feature">
                            <Icon name="copy" size="huge" />
                            <h3>{t('creatorDuplicateFeature1Title')}</h3>
                            <p>{t('creatorDuplicateFeature1Desc')}</p>
                        </div>
                        <div className="creator-duplicate-feature">
                            <Icon name="edit" size="huge" />
                            <h3>{t('creatorDuplicateFeature2Title')}</h3>
                            <p>{t('creatorDuplicateFeature2Desc')}</p>
                        </div>
                        <div className="creator-duplicate-feature">
                            <Icon name="share" size="huge" />
                            <h3>{t('creatorDuplicateFeature3Title')}</h3>
                            <p>{t('creatorDuplicateFeature3Desc')}</p>
                        </div>
                    </div>
                </Container>
                <button 
                    className="scroll-indicator section-scroll" 
                    onClick={() => document.querySelector('.creator-steps-section')?.scrollIntoView({ behavior: 'smooth' })}
                    aria-label="Scroll down"
                >
                    <Icon name="chevron down" />
                </button>
            </section>

            {/* Steps Section */}
            <section className="creator-steps-section">
                <Container>
                    <h2 className="creator-section-heading">{t('creatorStepsTitle')}</h2>
                    <div className="creator-steps-container">
                        {steps.map((step, index) => (
                            <div className="creator-step-item" key={index}>
                                <div className="creator-step-number">{index + 1}</div>
                                <div className="creator-step-content">
                                    <h3 className="creator-step-title">{step.title}</h3>
                                    <p className="creator-step-desc">{step.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </Container>
            </section>
        </div>
    )
}

export default CreatorLandingPage